import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import {
  GetListParams,
  GetListResponse,
  CreateResponse,
  CreateParams,
  UpdateParams,
  UpdateResponse,
  GetOneResponse,
  GetOneParams,
  DeleteOneParams,
  DeleteOneResponse,
  IBaseQueryFn,
  DataProviderDefinition
} from 'app/context/data/types';
import { RootState } from 'app/store';
import { generateSort } from 'app/utils/generate/generateSorters';
import { generateFilter } from 'app/utils/generate/gnerateFilters';
import { stringify } from 'qs';

type MethodTypes = 'get' | 'delete' | 'head' | 'options';
type MethodTypesWithBody = 'post' | 'put' | 'patch';

const replacePathParams = (path, params) => {
  return path.replace(/:(\w+)/g, (_, key) => {
    if (!params[key]) {
      throw new Error(`Missing parameter ${key} in path ${path}`);
    }
    return params[key];
  });
};

const baseQuery = fetchBaseQuery({
  baseUrl: `${process.env.REACT_APP_BASE_URL}/${process.env.REACT_APP_API_V1_VERSION}/micro`,
  headers: { Accept: '*/*', 'Content-type': 'application/json' },
  prepareHeaders: (headers, { getState }) => {
    // By default, if we have a token in the store, let's use that for authenticated requests
    const token = (getState() as RootState).auth.token;
    if (token) {
      // headers.set('authentication', `Bearer ${token}`);
      headers.set('Authorization', `${token}`);
    }
    return headers;
  }
});

export const MicroDataProvider = createApi<
  IBaseQueryFn,
  DataProviderDefinition,
  string
>({
  reducerPath: 'MicroDataProvider',
  baseQuery: baseQuery,
  refetchOnMountOrArgChange: true,
  endpoints: (builder) => ({
    getList: builder.query<GetListResponse, GetListParams>({
      query: ({ resource, pagination, filters, sorters, meta }) => {
        const { headers, method, id } = meta ?? {};
        const requestMethod = (method as MethodTypes) || 'GET';
        const queryFilters = generateFilter(filters);
        const querySorters = generateSort(sorters);
        const queryPagination = pagination
          ? {
              pageNo: pagination.current,
              pageSize: pagination.pageSize
            }
          : {};
        const combineQuery = {
          ...queryFilters,
          ...querySorters,
          ...queryPagination
        };
        const queryString = stringify(combineQuery, {
          encodeValuesOnly: false,
          indices: false
        });
        const urlWithId = replacePathParams(resource, { id });
        const urlWithQuery = Object.keys(combineQuery).length
          ? `${urlWithId}?${queryString}`
          : urlWithId;
        return {
          url: urlWithQuery,
          method: requestMethod,
          headers: headers
        };
      }
    }),
    getOne: builder.query<GetOneResponse, GetOneParams>({
      query: ({ resource, id, meta }) => {
        const { headers, method } = meta ?? {};
        const requestMethod = (method as MethodTypes) || 'GET';
        return {
          url: `${resource}/${id}`,
          method: requestMethod,
          headers: headers
        };
      }
    }),
    create: builder.mutation<CreateResponse, CreateParams>({
      query: ({ resource, variables, meta }) => {
        const { headers, method } = meta ?? {};
        const requestMethod = (method as MethodTypesWithBody) || 'POST';
        return {
          url: resource,
          method: requestMethod,
          headers: headers,
          body: variables
        };
      }
    }),
    update: builder.mutation<UpdateResponse, UpdateParams>({
      query: ({ resource, variables, id, meta }) => {
        const { headers, method } = meta ?? {};
        const requestMethod = (method as MethodTypesWithBody) || 'PUT';
        const url = `${resource}/${id}`;
        return {
          url: url,
          method: requestMethod,
          headers: headers,
          body: variables
        };
      }
    }),
    delete: builder.mutation<DeleteOneResponse, DeleteOneParams>({
      query: ({ resource, id, variables, meta }) => {
        const { headers, method } = meta ?? {};
        const requestMethod = (method as MethodTypes) || 'DELETE';
        const url = `${resource}/${id}`;
        return {
          url: url,
          body: variables,
          method: requestMethod,
          headers: headers
        };
      }
    })
  })
});
